import React from "react";
import { useTranslation } from "react-i18next";
import { X, UserPlus, Search, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../../../components/ui/Dialog";
import {
  AssetRecommendationRowView,
  AssetRecommendationEmployeeView,
  EmployeeSimpleView,
  searchEmployees,
  setEquipmentRequirementOverride,
  clearEquipmentRequirementOverride,
} from "../../../../api/General/assets";
import Button from "@/components/ui/Button";
import { EquipmentPriority } from "../../../../api/General/businessRoles";
import { Spinner } from "@/components/ui/Spinner";
import { Switch } from "@/components/ui/Switch";
import { Tooltip } from "@/components/ui/Tooltip";
import AiGeneratedBadge from "@/components/ui/AiGeneratedBadge";
import { useToast, ToastRenderer } from "@/hooks/useToast";
import { PriorityChip, SourceChip, RequirementLabel, WaiverBadge, priorityOrder, priorityLabelKey } from '@/components/assets/equipmentRequirementChrome';

interface Props {
  row: AssetRecommendationRowView | null;
  onClose: () => void;
  onChanged: () => Promise<void>;
  /** The SKU pick lives in AssetOverviewTab so it survives this drawer closing and reaches Transfer. */
  selectedSkuFor: (categoryId: number, employeeId: string) => number | undefined;
  onSelectSku: (categoryId: number, employeeId: string, skuCategoryId: number) => void;
}

const NOTE_MAX = 500;

// Two-letter monogram from the name; the drawer has no avatar image to show.
function initials(name?: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts.slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");
}

export default function AssetRecommendationDrawer({ row, onClose, onChanged, selectedSkuFor, onSelectSku }: Props) {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();
  const open = row !== null;

  const [pendingEmployeeId, setPendingEmployeeId] = React.useState<string | null>(null);
  const [confirmingEmployeeId, setConfirmingEmployeeId] = React.useState<string | null>(null);
  const [note, setNote] = React.useState("");
  const [addOpen, setAddOpen] = React.useState(false);
  const [addSearch, setAddSearch] = React.useState("");
  const [addResults, setAddResults] = React.useState<EmployeeSimpleView[]>([]);
  const [addSearching, setAddSearching] = React.useState(false);
  const [addingId, setAddingId] = React.useState<string | null>(null);
  const [editingEmployeeId, setEditingEmployeeId] = React.useState<string | null>(null);
  const [editPriority, setEditPriority] = React.useState<EquipmentPriority>(EquipmentPriority.Important);
  const [editQuantity, setEditQuantity] = React.useState(1);

  const categoryId = row?.categoryId ?? null;

  React.useEffect(() => {
    setPendingEmployeeId(null);
    setConfirmingEmployeeId(null);
    setNote("");
    setAddOpen(false);
    setAddSearch("");
    setAddResults([]);
    setEditingEmployeeId(null);
  }, [categoryId]);

  // Grouping by department IS the department information — no separate "Department:" tile, which
  // would be wrong whenever a recommendation spans departments.
  const groups = React.useMemo(() => {
    if (!row) return [] as [string, AssetRecommendationEmployeeView[]][];
    const map = new Map<string, AssetRecommendationEmployeeView[]>();
    row.employees.forEach((e) => {
      const key = e.departmentName || String(t("assetOverview.drawerNoDepartment"));
      const bucket = map.get(key);
      if (bucket) bucket.push(e);
      else map.set(key, [e]);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [row, t]);

  const hasAddedRow = (row?.employees ?? []).some((e) => e.isAddedByAssetManager);

  const skuFor = (e: AssetRecommendationEmployeeView): number | undefined =>
    (row ? selectedSkuFor(row.categoryId, e.employeeId) : undefined) ?? e.suggestedCategoryId ?? row?.skus[0]?.categoryId;

  const isAiSku = (e: AssetRecommendationEmployeeView) =>
    row !== null && selectedSkuFor(row.categoryId, e.employeeId) === undefined && e.suggestedCategoryId != null;

  const runOverride = async (employeeId: string, work: () => Promise<{ errors: { description?: string }[] }>, successKey: string): Promise<boolean> => {
    if (pendingEmployeeId !== null) return false;
    setPendingEmployeeId(employeeId);
    try {
      const result = await work();
      if (result.errors.length === 0) {
        showToast(t(successKey), "success");
        setConfirmingEmployeeId(null);
        setNote("");
        await onChanged();
        return true;
      }
      showToast(result.errors[0]?.description || t("assetOverview.overrideError"), "error");
      return false;
    } catch {
      showToast(t("assetOverview.overrideError"), "error");
      return false;
    } finally {
      setPendingEmployeeId(null);
    }
  };

  /**
   * The server assigns PriorityOverride and QuantityOverride on EVERY upsert, so a call that leaves
   * them out does not preserve them, it CLEARS them. Echoing the row's current overrides back is what
   * stops a cross off, a restore or a counts toggle from silently resetting an Essential / quantity 2
   * requirement to the Important / 1 fallbacks.
   */
  const keptOverrides = (e: AssetRecommendationEmployeeView) => ({
    priority: e.isPriorityOverridden ? e.priority : undefined,
    quantity: e.isQuantityOverridden ? e.quantity : undefined,
  });

  const crossOff = (e: AssetRecommendationEmployeeView) =>
    runOverride(e.employeeId, () => setEquipmentRequirementOverride({
      employeeId: e.employeeId,
      categoryId: row!.categoryId,
      countsTowardCoverage: false,
      isAdded: e.isAddedByAssetManager,
      note: note.trim() || undefined,
      ...keptOverrides(e),
    }), "assetOverview.crossOffDone");

  // A derived requirement is restored by dropping the override; an added one has to stay recorded.
  const restore = (e: AssetRecommendationEmployeeView) =>
    runOverride(e.employeeId, () => (e.isAddedByAssetManager
      ? setEquipmentRequirementOverride({
        employeeId: e.employeeId,
        categoryId: row!.categoryId,
        countsTowardCoverage: true,
        isAdded: true,
        ...keptOverrides(e),
      })
      : clearEquipmentRequirementOverride(e.employeeId, row!.categoryId)), "assetOverview.restoreDone");

  const setCounts = (e: AssetRecommendationEmployeeView, counts: boolean) =>
    runOverride(e.employeeId, () => setEquipmentRequirementOverride({
      employeeId: e.employeeId,
      categoryId: row!.categoryId,
      countsTowardCoverage: counts,
      isAdded: true,
      ...keptOverrides(e),
    }), "assetOverview.countsUpdated");

  // Modify: the importance and quantity that actually weigh in the coverage score.
  const saveRequirement = (e: AssetRecommendationEmployeeView) =>
    runOverride(e.employeeId, () => setEquipmentRequirementOverride({
      employeeId: e.employeeId,
      categoryId: row!.categoryId,
      countsTowardCoverage: e.countsTowardCoverage,
      isAdded: e.isAddedByAssetManager,
      priority: editPriority,
      quantity: editQuantity,
    }), "assetOverview.requirementUpdated").then((ok) => { if (ok) setEditingEmployeeId(null); });

  // Drops both overrides and falls back to what the role or goal declared.
  const clearRequirementOverride = (e: AssetRecommendationEmployeeView) =>
    runOverride(e.employeeId, () => setEquipmentRequirementOverride({
      employeeId: e.employeeId,
      categoryId: row!.categoryId,
      countsTowardCoverage: e.countsTowardCoverage,
      isAdded: e.isAddedByAssetManager,
    }), "assetOverview.requirementReset").then((ok) => { if (ok) setEditingEmployeeId(null); });

  const openEditor = (e: AssetRecommendationEmployeeView) => {
    setEditingEmployeeId(e.employeeId);
    setEditPriority(e.priority);
    setEditQuantity(e.quantity ?? 1);
  };

  React.useEffect(() => {
    if (!addOpen) return;
    let cancelled = false;
    setAddSearching(true);
    const timer = setTimeout(async () => {
      try {
        const result = await searchEmployees(addSearch.trim() || undefined);
        if (!cancelled) setAddResults(result.errors.length === 0 ? result.payload : []);
      } catch {
        if (!cancelled) setAddResults([]);
      } finally {
        if (!cancelled) setAddSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [addOpen, addSearch]);

  const alreadyListed = React.useMemo(
    () => new Set((row?.employees ?? []).map((e) => e.employeeId)),
    [row]
  );

  const addEmployee = async (picked: EmployeeSimpleView) => {
    if (!row || addingId !== null) return;
    setAddingId(picked.id);
    try {
      const result = await setEquipmentRequirementOverride({
        employeeId: picked.id,
        categoryId: row.categoryId,
        countsTowardCoverage: true,
        isAdded: true,
      });
      if (result.errors.length === 0) {
        showToast(t("assetOverview.addEmployeeAdded", { name: picked.fullName }), "success");
        setAddOpen(false);
        setAddSearch("");
        await onChanged();
      } else {
        showToast(result.errors[0]?.description || t("assetOverview.overrideError"), "error");
      }
    } catch {
      showToast(t("assetOverview.overrideError"), "error");
    } finally {
      setAddingId(null);
    }
  };

  const inputClasses = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors";

  // Radix Dialog owns the overlay (click-outside closes), Esc, focus trap and scroll lock.
  //
  // This is an EDGE-DOCKED panel, not a centered dialog. It used to pass size="full", which in this
  // codebase means max-w-[calc(100vw-2rem)] — a centred sheet spanning the entire viewport for what
  // is usually a handful of name rows. The positioning recipe below (translate reset +
  // ltr:right-0 / rtl:left-0 + rounded-none) mirrors PublicProfileDisplay/ContactDrawer, the
  // established side-drawer pattern here: `rtl:left-0` outranks `left-auto` on specificity, which is
  // what docks it to the correct edge in Arabic. It stays w-full below sm, so mobile is unchanged.
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        hideCloseButton
        className="fixed top-0 left-auto ltr:right-0 rtl:left-0 translate-x-0 translate-y-0 h-full max-h-full w-full sm:w-[440px] md:w-[500px] lg:w-[540px] lg:max-w-[50vw] rounded-none border-y-0 ltr:border-r-0 rtl:border-l-0 shadow-2xl flex flex-col overflow-hidden p-0 ltr:data-[state=open]:slide-in-from-right rtl:data-[state=open]:slide-in-from-left ltr:data-[state=closed]:slide-out-to-right rtl:data-[state=closed]:slide-out-to-left"
        data-testid="overview-drawer"
      >
        {row && (
          <>
            <div className="shrink-0 border-b border-border bg-card px-6 py-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <DialogTitle className="truncate text-lg font-semibold leading-tight text-foreground">
                    {row.categoryName}
                  </DialogTitle>
                  {/* Radix wants a description for aria-describedby; the heading and chips below
                      already carry it visually, so this is the screen-reader copy only. */}
                  <DialogDescription className="sr-only">
                    {t("assetOverview.drawerFooter", { count: row.employees.length, sku: row.categoryName })}
                  </DialogDescription>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <PriorityChip priority={row.priority} />
                    <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground-strong">
                      {t("assetOverview.availableCount", { count: row.available })}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t("assetOverview.drawerClose")}
                  className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="overview-drawer-close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              <p className="mb-6 text-xs leading-relaxed text-muted-foreground" data-testid="drawer-how-it-works">
                {t("assetOverview.drawerHowItWorks")}
                {hasAddedRow && ` ${t("assetOverview.drawerHowItWorksAdded")}`}
              </p>
              <div className="space-y-7">
              {groups.map(([department, employees]) => (
                <section key={department}>
                  <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t("assetOverview.drawerDepartment", { name: department, count: employees.length })}
                  </h3>
                  <ul className="mt-2.5 divide-y divide-border">
                    {employees.map((e) => {
                      const busy = pendingEmployeeId === e.employeeId;
                      const confirming = confirmingEmployeeId === e.employeeId;
                      const editing = editingEmployeeId === e.employeeId;
                      const selectedSku = skuFor(e);
                      return (
                        <li
                          key={e.employeeId}
                          className="py-3 first:pt-0 last:pb-0"
                          data-testid={`overview-drawer-employee-${e.employeeId}`}
                        >
                          <div className="flex items-start gap-3">
                            <span
                              className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-[11px] font-semibold ${
                                e.isWaived ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"
                              }`}
                              aria-hidden="true"
                            >
                              {initials(e.employeeName)}
                            </span>

                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                                <RequirementLabel label={e.employeeName} state={e} />
                                <PriorityChip priority={e.priority} />
                                {(e.isPriorityOverridden || e.isQuantityOverridden) && (
                                  <Tooltip content={t("assetOverview.requirementOverriddenTooltip")}>
                                    <span
                                      className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground-strong"
                                      data-testid={`drawer-overridden-${e.employeeId}`}
                                    >
                                      {t("assetOverview.importanceOverridden")}
                                    </span>
                                  </Tooltip>
                                )}
                                {(e.quantity ?? 1) > 1 && (
                                  <span className="text-xs tabular-nums text-muted-foreground" data-testid={`drawer-qty-${e.employeeId}`}>
                                    {t("assets.coverage.qtyNeeded", { needed: e.quantity })}
                                  </span>
                                )}
                                <WaiverBadge state={e} />
                                {e.isAddedByAssetManager && <SourceChip source="added" />}
                              </div>

                              {e.reason && (
                                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{e.reason}</p>
                              )}

                              {/* One SKU is not a choice, and the row header already names it. */}
                              {row.skus.length > 1 && (
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                  <select
                                    aria-label={t("assetOverview.suggestedSku")}
                                    value={selectedSku ?? ""}
                                    onChange={(ev) => onSelectSku(row.categoryId, e.employeeId, Number(ev.target.value))}
                                    className="max-w-full rounded-md border border-input bg-background px-2 py-1 text-xs text-foreground transition-colors focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                                    data-testid={`drawer-sku-${e.employeeId}`}
                                  >
                                    {row.skus.map((s) => (
                                      <option key={s.categoryId} value={s.categoryId}>
                                        {t("assetOverview.skuOption", { name: s.skuName, count: s.available })}
                                      </option>
                                    ))}
                                  </select>
                                  <AiGeneratedBadge visible={isAiSku(e)} />
                                  {e.skuRationale && (
                                    <span className="text-xs text-muted-foreground">{e.skuRationale}</span>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* One control per row, chosen by origin. An added requirement IS its
                                counts-toward-coverage flag, so a Cross off button beside the switch
                                would be two labels writing the same field. */}
                            <div className="flex shrink-0 items-center gap-1">
                              {busy && <Spinner size="sm" className="me-2" />}
                              <Tooltip content={t("assetOverview.editRequirementTooltip")}>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => (editing ? setEditingEmployeeId(null) : openEditor(e))}
                                  aria-label={t("assetOverview.editRequirement")}
                                  aria-expanded={editing}
                                  className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50"
                                  data-testid={`drawer-edit-${e.employeeId}`}
                                >
                                  <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                                </button>
                              </Tooltip>
                              {e.isAddedByAssetManager ? (
                                <Tooltip content={t(e.countsTowardCoverage
                                  ? "assetOverview.countsTowardCoverageOnTooltip"
                                  : "assetOverview.countsTowardCoverageOffTooltip")}>
                                  <span className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">
                                      {t("assetOverview.countsTowardCoverage")}
                                    </span>
                                    <Switch
                                      checked={e.countsTowardCoverage}
                                      disabled={busy}
                                      onCheckedChange={(v) => setCounts(e, v)}
                                      aria-label={t("assetOverview.countsTowardCoverage")}
                                      data-testid={`drawer-counts-${e.employeeId}`}
                                    />
                                  </span>
                                </Tooltip>
                              ) : (
                                <Tooltip content={e.isWaived ? t("assetOverview.restoreTooltip") : t("assetOverview.crossOffTooltip")}>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={busy}
                                    onClick={() => (e.isWaived ? restore(e) : setConfirmingEmployeeId(confirming ? null : e.employeeId))}
                                    data-testid={`drawer-crossoff-${e.employeeId}`}
                                  >
                                    {e.isWaived ? t("assetOverview.restore") : t("assetOverview.crossOff")}
                                  </Button>
                                </Tooltip>
                              )}
                            </div>
                          </div>

                          {editing && (
                            <div
                              className="mt-2.5 ms-11 flex flex-wrap items-end gap-2"
                              data-testid={`drawer-edit-panel-${e.employeeId}`}
                            >
                              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                                {t("assetOverview.addRequirementPriority")}
                                <select
                                  value={editPriority}
                                  onChange={(ev) => setEditPriority(ev.target.value as EquipmentPriority)}
                                  className={`${inputClasses} py-1 text-xs`}
                                  data-testid={`drawer-edit-priority-${e.employeeId}`}
                                >
                                  {priorityOrder.map((p) => (
                                    <option key={p} value={p}>{t(priorityLabelKey[p])}</option>
                                  ))}
                                </select>
                              </label>
                              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                                {t("assetOverview.addRequirementQuantity")}
                                <input
                                  type="number"
                                  min={1}
                                  max={99}
                                  value={editQuantity}
                                  onChange={(ev) => setEditQuantity(Math.max(1, Math.min(99, Number(ev.target.value) || 1)))}
                                  className={`${inputClasses} w-20 py-1 text-xs tabular-nums`}
                                  data-testid={`drawer-edit-quantity-${e.employeeId}`}
                                />
                              </label>
                              <Button size="sm" disabled={busy} onClick={() => saveRequirement(e)} data-testid={`drawer-edit-save-${e.employeeId}`}>
                                {t("Save")}
                              </Button>
                              {(e.isPriorityOverridden || e.isQuantityOverridden) && (
                                <Tooltip content={t("assetOverview.useDeclaredTooltip")}>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={busy}
                                    onClick={() => clearRequirementOverride(e)}
                                    data-testid={`drawer-edit-reset-${e.employeeId}`}
                                  >
                                    {t("assetOverview.useDeclared")}
                                  </Button>
                                </Tooltip>
                              )}
                              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditingEmployeeId(null)}>
                                {t("Cancel")}
                              </Button>
                            </div>
                          )}

                          {confirming && !e.isWaived && (
                            <div className="mt-2.5 ms-11 space-y-2" data-testid={`drawer-crossoff-confirm-${e.employeeId}`}>
                              <div className="flex flex-wrap items-center gap-2">
                              <input
                                type="text"
                                autoFocus
                                value={note}
                                maxLength={NOTE_MAX}
                                onChange={(ev) => setNote(ev.target.value)}
                                placeholder={t("assetOverview.crossOffNotePlaceholder")}
                                aria-label={t("assetOverview.crossOffNotePlaceholder")}
                                className={`${inputClasses} min-w-0 flex-1`}
                                data-testid={`drawer-crossoff-note-${e.employeeId}`}
                              />
                              <Button
                                size="sm"
                                disabled={busy}
                                onClick={() => crossOff(e)}
                                data-testid={`drawer-crossoff-submit-${e.employeeId}`}
                              >
                                {t("assetOverview.crossOffConfirm")}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={busy}
                                onClick={() => { setConfirmingEmployeeId(null); setNote(""); }}
                              >
                                {t("Cancel")}
                              </Button>
                              </div>
                              <p className="text-xs text-muted-foreground" data-testid={`drawer-crossoff-lift-note-${e.employeeId}`}>
                                {t("assetOverview.crossOffLiftNote")}
                              </p>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
              </div>
            </div>

            <div className="shrink-0 border-t border-border bg-card px-6 py-4">
              {addOpen ? (
                <div className="space-y-2.5" data-testid="drawer-add-panel">
                  {/* Adding here changes the coverage score, which the search box alone does not say. */}
                  <p className="text-xs leading-relaxed text-muted-foreground" data-testid="drawer-add-explainer">
                    {t("assetOverview.addEmployeeExplainer", { category: row.categoryName })}
                  </p>
                  <div className="relative">
                    <Search className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground ltr:left-3 rtl:right-3" aria-hidden="true" />
                    <input
                      type="text"
                      autoFocus
                      value={addSearch}
                      onChange={(ev) => setAddSearch(ev.target.value)}
                      placeholder={t("assetOverview.addEmployeeSearch")}
                      aria-label={t("assetOverview.addEmployeeSearch")}
                      className={`${inputClasses} ltr:pl-9 rtl:pr-9`}
                      data-testid="drawer-add-search"
                    />
                  </div>
                  {addSearching ? (
                    <div className="flex justify-center py-3"><Spinner size="sm" /></div>
                  ) : (
                    <ul className="max-h-44 space-y-0.5 overflow-y-auto">
                      {addResults.filter((r) => !alreadyListed.has(r.id)).map((r) => (
                        <li key={r.id}>
                          <button
                            type="button"
                            onClick={() => addEmployee(r)}
                            disabled={addingId !== null}
                            className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-start text-sm text-foreground transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50"
                            data-testid={`drawer-add-result-${r.id}`}
                          >
                            <span className="min-w-0 truncate">
                              {r.fullName}
                              {r.department && <span className="ms-2 text-xs text-muted-foreground">{r.department}</span>}
                            </span>
                            {addingId === r.id && <Spinner size="sm" />}
                          </button>
                        </li>
                      ))}
                      {addResults.filter((r) => !alreadyListed.has(r.id)).length === 0 && (
                        <li className="px-2 py-3 text-center text-xs text-muted-foreground" data-testid="drawer-add-empty">
                          {t("assetOverview.addEmployeeNoResults")}
                        </li>
                      )}
                    </ul>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => { setAddOpen(false); setAddSearch(""); }}>
                    {t("Cancel")}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="min-w-0 text-xs text-muted-foreground">
                    {t("assetOverview.drawerFooter", { count: row.employees.length, sku: row.categoryName })}
                  </p>
                  <Tooltip content={t("assetOverview.addEmployeeTooltip")}>
                    <Button size="sm" variant="outline" onClick={() => setAddOpen(true)} data-testid="drawer-add-employee">
                      <UserPlus className="h-3.5 w-3.5 me-2" aria-hidden="true" />
                      {t("assetOverview.addEmployee")}
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>
            <ToastRenderer toast={toast} onDismiss={dismissToast} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
